// Portal del paciente (autoservicio, solo lectura). Ruta /portal/inicio.
// El paciente logueado ve SOLO su información (RLS por partner_id del JWT).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CalendarPlus, CreditCard, FileText, FlaskConical, HeartPulse, Loader2, Pill, Star, Syringe, Video } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  AutoTextarea,
  Campo, ESTADO_CITA, ESTADO_TELE, MedicoRow, MiSolicitudReceta, Modal, PortalCita, Slot, TelePacienteRow,
  TIPO_ATENCION, calcEdad, fieldCls, hhmm, isoDate, medRpc,
} from './shared';
import { ChatTele, ConsentimientoTele } from './Teleconsulta';
import { VideoSala } from './VideoSala';
import { ModalPagoTele, PagoBadge } from './TelePago';
import { Mensajes } from './Mensajes';
import { ConsentimientosPaciente } from './Consentimientos';
import { PanelArcoPaciente } from './DerechosArco';
import { AvisoPrivacidadModal } from '@/features/admin/AvisoPrivacidad';
import { LOCALE } from '@/lib/utils/regional';

interface PortalData {
  paciente: { razon_social: string; identificacion: string | null; tipo_sangre: string | null; fecha_nacimiento: string | null } | null;
  proximas_citas: Array<{ fecha: string; hora_inicio: string; estado: string; tipo_atencion: string; medico: string; especialidad: string | null }>;
  historial_citas: Array<{ fecha: string; hora_inicio: string; estado: string; medico: string }>;
  evoluciones: Array<{ fecha: string; motivo: string | null; plan: string | null; medico: string }>;
  recetas: Array<{ id: string; codigo: string; fecha: string; estado: string; renovable: boolean; medico: string; items: number }>;
  resultados: Array<{ id: string; codigo: string; fecha: string; estado: string; fecha_resultado: string | null; items: number }>;
  problemas: string[];
  vacunas: Array<{ vacuna: string; dosis: string | null; fecha: string }>;
}

export function PortalPaciente() {
  const [verAviso, setVerAviso] = useState(false);
  const { data, isFetching } = useQuery<PortalData>({
    queryKey: ['med-portal'], queryFn: () => medRpc<PortalData>('med_portal', {}),
  });

  if (isFetching) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  if (!data?.paciente) return <div className="p-6 text-center text-muted-foreground">No se encontró tu ficha de paciente.</div>;
  const p = data.paciente;

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <Card>
        <CardContent className="py-4 flex items-center gap-3">
          <div className="rounded-full bg-primary/10 text-primary p-3"><HeartPulse className="w-6 h-6" /></div>
          <div>
            <h1 className="font-display text-2xl font-semibold leading-tight">{p.razon_social}</h1>
            <p className="text-sm text-muted-foreground"><span className="font-mono">{p.identificacion ?? 's/i'}</span> · {calcEdad(p.fecha_nacimiento)} · {p.tipo_sangre ?? 'Sangre N/D'}</p>
          </div>
        </CardContent>
      </Card>

      <Teleconsultas />

      <Card>
        <CardContent className="pt-4 space-y-5">
          <ConsentimientosPaciente portal />
          <PanelArcoPaciente portal nombre={p.razon_social} />
        </CardContent>
      </Card>

      <Mensajes embedded />

      <MisCitas />

      <div className="grid gap-4 lg:grid-cols-2">
        <SolicitarRecetaCard />
        <PagoStub />

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Problemas de salud activos</CardTitle></CardHeader>
          <CardContent>
            {data.problemas.length === 0 ? <Vacio t="Sin problemas registrados" /> : (
              <ul className="list-disc pl-5 text-sm space-y-1">{data.problemas.map((pr, i) => <li key={i}>{pr}</li>)}</ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><FileText className="w-4 h-4" /> Evoluciones recientes</CardTitle></CardHeader>
          <CardContent>
            {data.evoluciones.length === 0 ? <Vacio t="Sin evoluciones" /> : (
              <ul className="divide-y text-sm">
                {data.evoluciones.map((ev, i) => (
                  <li key={i} className="py-2">
                    <div className="font-medium">{new Date(ev.fecha).toLocaleDateString(LOCALE)} · {ev.medico}</div>
                    {ev.motivo && <div className="text-muted-foreground">{ev.motivo}</div>}
                    {ev.plan && <div className="text-xs"><b>Plan:</b> {ev.plan}</div>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><Pill className="w-4 h-4" /> Recetas</CardTitle></CardHeader>
          <CardContent>
            {data.recetas.length === 0 ? <Vacio t="Sin recetas" /> : (
              <ul className="divide-y text-sm">
                {data.recetas.map((r) => (
                  <li key={r.id} className="flex items-center justify-between py-2">
                    <span><b>{r.codigo}</b> · {new Date(r.fecha).toLocaleDateString(LOCALE)} · {r.medico} <span className="text-muted-foreground">({r.items} med.)</span></span>
                    <span className={`rounded px-2 py-0.5 text-xs ${r.estado === 'anulada' ? 'bg-urgente-soft text-urgente' : 'bg-estable-soft text-estable'}`}>{r.estado}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><FlaskConical className="w-4 h-4" /> Resultados de exámenes</CardTitle></CardHeader>
          <CardContent>
            {data.resultados.length === 0 ? <Vacio t="Sin resultados disponibles" /> : (
              <ul className="divide-y text-sm">
                {data.resultados.map((r) => (
                  <li key={r.id} className="flex items-center justify-between py-2">
                    <span><b>{r.codigo}</b> · {new Date(r.fecha).toLocaleDateString(LOCALE)} <span className="text-muted-foreground">({r.items} exam.)</span></span>
                    <span className={`rounded px-2 py-0.5 text-xs ${r.estado === 'entregada' ? 'bg-completado-soft text-completado' : 'bg-estable-soft text-estable'}`}>{r.estado}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><Syringe className="w-4 h-4" /> Vacunación</CardTitle></CardHeader>
          <CardContent>
            {data.vacunas.length === 0 ? <Vacio t="Sin registros de vacunas" /> : (
              <ul className="divide-y text-sm">
                {data.vacunas.map((v, i) => <li key={i} className="flex justify-between py-2"><span>{v.vacuna} {v.dosis && <span className="text-muted-foreground">· {v.dosis}</span>}</span><span className="text-muted-foreground">{v.fecha}</span></li>)}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {data.historial_citas.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Historial de citas</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y text-sm">
              {data.historial_citas.map((c, i) => {
                const e = ESTADO_CITA[c.estado] ?? ESTADO_CITA.programada;
                return <li key={i} className="flex items-center justify-between py-1.5"><span>{c.fecha} {hhmm(c.hora_inicio)} · {c.medico}</span><span className={`rounded px-2 py-0.5 text-xs ${e.cls}`}>{e.label}</span></li>;
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      <footer className="pt-2 text-center text-xs text-muted-foreground">
        <button className="underline hover:text-primary" onClick={() => setVerAviso(true)}>Aviso de privacidad</button>
      </footer>
      {verAviso && <AvisoPrivacidadModal onClose={() => setVerAviso(false)} />}
    </div>
  );
}

function Vacio({ t }: { t: string }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{t}</p>;
}

// ---- Mis citas (agendar / cancelar / calificar) ----
function MisCitas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: citas = [] } = useQuery<PortalCita[]>({ queryKey: ['med-portal-citas'], queryFn: () => medRpc<PortalCita[]>('med_portal_citas', {}), refetchInterval: 15000 });
  const [agendar, setAgendar] = useState(false);
  const [calificar, setCalificar] = useState<PortalCita | null>(null);
  const refrescar = () => { void qc.invalidateQueries({ queryKey: ['med-portal-citas'] }); void qc.invalidateQueries({ queryKey: ['med-portal'] }); };

  const cancelar = useMutation({
    mutationFn: (id: string) => medRpc('med_portal_cancelar_cita', { _cita_id: id }),
    onSuccess: () => { toast.success('Cita cancelada'); refrescar(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card>
      {dlg.ui}
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-1.5"><CalendarClock className="w-4 h-4" /> Mis citas</CardTitle>
        <Button size="sm" onClick={() => setAgendar(true)}><CalendarPlus className="w-4 h-4 mr-1" /> Agendar cita</Button>
      </CardHeader>
      <CardContent>
        {citas.length === 0 ? <Vacio t="No tienes citas" /> : (
          <ul className="divide-y text-sm">
            {citas.map((c) => {
              const e = ESTADO_CITA[c.estado] ?? ESTADO_CITA.programada;
              return (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span><b>{c.fecha}</b> {hhmm(c.hora_inicio)} · {c.medico}{c.especialidad ? ` (${c.especialidad})` : ''} {c.tipo_atencion === 'telematica' && <span className="text-violet-600">· telemática</span>}</span>
                  <span className="flex items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${e.cls}`}>{e.label}</span>
                    {c.cancelable && <Button size="sm" variant="outline" onClick={async () => { if (!(await dlg.confirmar('¿Cancelar esta cita?', { peligro: true }))) return; cancelar.mutate(c.id); }}>Cancelar</Button>}
                    {c.estado === 'atendida' && !c.calificada && <Button size="sm" variant="outline" onClick={() => setCalificar(c)}><Star className="w-4 h-4 mr-1" /> Calificar</Button>}
                    {c.calificada && <span className="text-xs text-estable">★ calificada</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      {agendar && <AgendarCita onClose={() => setAgendar(false)} onSaved={() => { setAgendar(false); refrescar(); }} />}
      {calificar && <Calificar cita={calificar} onClose={() => setCalificar(null)} onSaved={() => { setCalificar(null); refrescar(); }} />}
    </Card>
  );
}

function AgendarCita({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });
  const [medicoId, setMedicoId] = useState('');
  const [fecha, setFecha] = useState(isoDate(new Date()));
  const [hora, setHora] = useState('');
  const [tipo, setTipo] = useState('presencial');
  const [motivo, setMotivo] = useState('');
  const { data: slots = [] } = useQuery<Slot[]>({
    queryKey: ['med-disp', medicoId, fecha], queryFn: () => medRpc<Slot[]>('med_disponibilidad', { _medico_id: medicoId, _fecha: fecha }), enabled: !!medicoId && !!fecha,
  });
  const libres = slots.filter((s) => s.disponible);
  const medico = medicos.find((m) => m.id === medicoId);

  const guardar = useMutation({
    mutationFn: () => medRpc('med_portal_agendar', { _data: { medico_id: medicoId, especialidad_id: medico?.especialidad_id ?? null, fecha, hora_inicio: hora, tipo_atencion: tipo, motivo: motivo || null } }),
    onSuccess: () => { toast.success('Cita agendada'); onSaved(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Agendar cita" onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Médico *">
          <select className={fieldCls} value={medicoId} onChange={(e) => { setMedicoId(e.target.value); setHora(''); }}>
            <option value="">— Selecciona —</option>
            {medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}{m.especialidad ? ` · ${m.especialidad}` : ''}{m.n_calificaciones ? ` · ★${m.rating}` : ''}</option>)}
          </select>
        </Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Fecha *"><input type="date" className={fieldCls} value={fecha} min={isoDate(new Date())} onChange={(e) => { setFecha(e.target.value); setHora(''); }} /></Campo>
          <Campo label="Hora *">
            {medicoId && libres.length > 0 ? (
              <select className={fieldCls} value={hora} onChange={(e) => setHora(e.target.value)}>
                <option value="">— Cupo —</option>{libres.map((s) => <option key={s.hora_inicio} value={s.hora_inicio}>{s.hora_inicio}</option>)}
              </select>
            ) : <input type="time" className={fieldCls} value={hora} onChange={(e) => setHora(e.target.value)} />}
          </Campo>
        </div>
        {medicoId && fecha && libres.length === 0 && <p className="text-xs text-pendiente">El médico no tiene cupos libres ese día.</p>}
        <Campo label="Tipo">
          <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>{TIPO_ATENCION.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        </Campo>
        <Campo label="Motivo"><AutoTextarea value={motivo} onChange={(e) => setMotivo(e.target.value)} /></Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!medicoId || !fecha || !hora} onClick={() => guardar.mutate()}>Agendar</Button>
        </div>
      </div>
    </Modal>
  );
}

function Calificar({ cita, onClose, onSaved }: { cita: PortalCita; onClose: () => void; onSaved: () => void }) {
  const [estrellas, setEstrellas] = useState(0);  // sin preselección (evita sesgo al alza)
  const [comentario, setComentario] = useState('');
  const guardar = useMutation({
    mutationFn: () => medRpc('med_portal_calificar', { _cita_id: cita.id, _estrellas: estrellas, _comentario: comentario || null }),
    onSuccess: () => { toast.success('¡Gracias por tu calificación!'); onSaved(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title="Calificar la atención" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{cita.medico} · {cita.fecha}</p>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} aria-label={`${n} estrella${n > 1 ? 's' : ''}`} aria-pressed={n <= estrellas} onClick={() => setEstrellas(n)}><Star className={`w-7 h-7 ${n <= estrellas ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} /></button>
          ))}
        </div>
        <Campo label="Comentario"><AutoTextarea value={comentario} onChange={(e) => setComentario(e.target.value)} /></Campo>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancelar</Button><Button isLoading={guardar.isPending} disabled={estrellas === 0} onClick={() => guardar.mutate()}>Enviar</Button></div>
      </div>
    </Modal>
  );
}

// ---- Solicitar receta de continuación ----
function SolicitarRecetaCard() {
  const qc = useQueryClient();
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });
  const { data: mis = [] } = useQuery<MiSolicitudReceta[]>({ queryKey: ['med-mis-sol-receta'], queryFn: () => medRpc<MiSolicitudReceta[]>('med_mis_solicitudes_receta', {}), refetchInterval: 20000 });
  const [desc, setDesc] = useState('');
  const [medicoId, setMedicoId] = useState('');
  const crear = useMutation({
    mutationFn: () => medRpc('med_portal_solicitar_receta', { _data: { descripcion: desc, medico_id: medicoId || null } }),
    onSuccess: () => { setDesc(''); toast.success('Solicitud enviada'); void qc.invalidateQueries({ queryKey: ['med-mis-sol-receta'] }); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cls: Record<string, string> = { pendiente: 'bg-pendiente-soft text-pendiente', aprobada: 'bg-estable-soft text-estable', rechazada: 'bg-urgente-soft text-urgente' };

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><Pill className="w-4 h-4" /> Solicitar receta de continuación</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <AutoTextarea placeholder="¿Qué medicamento necesitas continuar?" value={desc} onChange={(e) => setDesc(e.target.value)} />
        <select className={fieldCls} value={medicoId} onChange={(e) => setMedicoId(e.target.value)}>
          <option value="">Médico (opcional)</option>{medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}
        </select>
        <Button size="sm" isLoading={crear.isPending} disabled={!desc.trim()} onClick={() => crear.mutate()}>Enviar solicitud</Button>
        {mis.length > 0 && (
          <ul className="divide-y text-sm pt-2 border-t">
            {mis.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-1.5">
                <span className="truncate">{s.descripcion}{s.respuesta && <span className="block text-xs text-muted-foreground italic">{s.respuesta}</span>}</span>
                <span className={`rounded px-2 py-0.5 text-xs capitalize ${cls[s.estado] ?? ''}`}>{s.estado}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PagoStub() {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><CreditCard className="w-4 h-4" /> Pago en línea</CardTitle></CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">El pago en línea de facturas estará disponible próximamente (pendiente de integración con la pasarela de pagos).</p>
      </CardContent>
    </Card>
  );
}

// ---- Teleconsultas del paciente (cola virtual + unirse + chat) ----
function Teleconsultas() {
  const { data: rows = [] } = useQuery<TelePacienteRow[]>({ queryKey: ['med-tele-paciente'], queryFn: () => medRpc<TelePacienteRow[]>('med_tele_paciente', {}), refetchInterval: 10000 });
  const qcTele = useQueryClient();
  const [sala, setSala] = useState<TelePacienteRow | null>(null);
  const [consent, setConsent] = useState<TelePacienteRow | null>(null);
  const [pagar, setPagar] = useState<TelePacienteRow | null>(null);

  if (rows.length === 0) return null;
  if (sala?.teleconsulta_id) {
    return (
      <Card>
        <CardContent className="pt-4">
          <VideoSala teleconsultaId={sala.teleconsulta_id} actor="paciente" onCerrar={() => setSala(null)}
            panelEspera={(
              <>
                <div className="px-3 py-2 border-b text-sm font-medium">Chat con el médico</div>
                <ChatTele teleconsultaId={sala.teleconsulta_id} />
              </>
            )} />
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><Video className="w-4 h-4" /> Mis teleconsultas</CardTitle></CardHeader>
      <CardContent>
        <ul className="divide-y text-sm">
          {rows.map((r) => {
            const e = ESTADO_TELE[r.estado] ?? ESTADO_TELE.programada;
            return (
              <li key={r.cita_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span>
                  <b>{r.fecha}</b> {hhmm(r.hora_inicio)} · {r.medico}{r.especialidad ? ` (${r.especialidad})` : ''}
                  {r.turno_antes > 0 && <span className="ml-2 text-pendiente">· {r.turno_antes} antes que tú</span>}
                </span>
                <span className="flex items-center gap-2">
                  <span className={`rounded px-2 py-0.5 text-xs ${e.cls}`}>{e.label}</span>
                  <PagoBadge estado={r.pago_estado} tarifa={r.tarifa} />
                  {(r.pago_estado === 'pendiente' || r.pago_estado === 'por_validar') && (
                    <Button size="sm" variant="outline" onClick={() => setPagar(r)}>Pagar</Button>
                  )}
                  {r.teleconsulta_id && !r.consentimiento_aceptado && <Button size="sm" variant="outline" onClick={() => setConsent(r)}>Consentimiento</Button>}
                  {r.teleconsulta_id && r.consentimiento_aceptado && (
                    <Button size="sm" disabled={r.pago_estado === 'pendiente' || r.pago_estado === 'por_validar'}
                      title={r.pago_estado === 'pendiente' ? 'Paga la teleconsulta para entrar' : r.pago_estado === 'por_validar' ? 'Tu pago está en validación' : undefined}
                      onClick={() => setSala(r)}><Video className="w-4 h-4 mr-1" /> Entrar a la consulta</Button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>

      {pagar && (
        <ModalPagoTele citaId={pagar.cita_id} tarifa={Number(pagar.tarifa ?? 0)}
          onClose={() => setPagar(null)}
          onPagado={() => { setPagar(null); void qcTele.invalidateQueries({ queryKey: ['med-tele-paciente'] }); }} />
      )}
      {consent?.teleconsulta_id && (
        <ConsentimientoTele teleconsultaId={consent.teleconsulta_id} onClose={() => setConsent(null)} onAceptado={() => setConsent(null)} />
      )}
    </Card>
  );
}
